import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSession } from '@/context/SessionContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, ChevronLeft, ChevronRight, Loader2, Upload,
  Download, BookOpen, Send, MessageSquare, Pencil, Info, Maximize2
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LearnerSequencePlayer = () => {
  const { seqId } = useParams();
  const navigate = useNavigate();
  const { selectedSessionId } = useSession();
  const { user } = useAuth();
  const [sequence, setSequence] = useState(null);
  const [discussions, setDiscussions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [newMsg, setNewMsg] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!selectedSessionId || !seqId) return;
    try {
      const res = await fetch(`${API}/learner/sessions/${selectedSessionId}/elearning/sequences/${seqId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSequence(data.sequence);
        setDiscussions(data.discussions || []);
        setCurrentIdx(data.sequence.current_activity_index || 0);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [selectedSessionId, seqId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const activities = sequence?.activities || [];
  const currentActivity = activities[currentIdx];
  const completedActs = sequence?.completed_activities || [];
  const progress = sequence?.progress || 0;
  const firstLetter = (sequence?.title || 'S')[0].toUpperCase();

  const goToActivity = async (idx) => {
    setCurrentIdx(idx);
    await fetch(`${API}/learner/sessions/${selectedSessionId}/elearning/sequences/${seqId}/progress`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ current_activity_index: idx }),
    }).catch(() => {});
  };

  const completeAndNext = async () => {
    if (currentActivity) {
      await fetch(`${API}/learner/sessions/${selectedSessionId}/elearning/sequences/${seqId}/progress`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activity_id: currentActivity.activity_id, action: 'complete', current_activity_index: currentIdx + 1 }),
      }).catch(() => {});
    }
    if (currentIdx < activities.length - 1) {
      setCurrentIdx(currentIdx + 1);
    }
    fetchDetail();
  };

  const sendMessage = async () => {
    if (!newMsg.trim()) return;
    setSendingMsg(true);
    try {
      const res = await fetch(`${API}/learner/sessions/${selectedSessionId}/elearning/sequences/${seqId}/discussion`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: newMsg }),
      });
      if (res.ok) {
        const msg = await res.json();
        setDiscussions(prev => [msg, ...prev]);
        setNewMsg('');
      }
    } catch (e) { toast.error('Erreur'); }
    setSendingMsg(false);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('activity_id', currentActivity?.activity_id || '');
      const res = await fetch(`${API}/learner/sessions/${selectedSessionId}/elearning/sequences/${seqId}/upload`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (res.ok) toast.success('Fichier envoyé !');
      else toast.error('Erreur d\'upload');
    } catch (e) { toast.error('Erreur d\'upload'); }
    setUploadingFile(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!sequence) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4">
        <BookOpen className="h-16 w-16 text-slate-300" />
        <p className="text-slate-600">Séquence introuvable</p>
        <Button onClick={() => navigate('/apprenant/sequences')}>Retour</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="sequence-player">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 h-[56px] bg-white border-b border-slate-100 flex items-center justify-between px-4" data-testid="player-header">
        <div className="flex items-center gap-3">
          <button className="lg:hidden w-8 h-8 flex items-center justify-center"><span className="text-xl">&#9776;</span></button>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center">
            <span className="text-white font-bold text-xs">A</span>
          </div>
          <span className="text-sm font-bold text-blue-700 hidden sm:block">Affea</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-blue-700 flex items-center justify-center text-white"><MessageSquare className="h-4 w-4" /></div>
          <div className="text-sm font-medium text-white bg-blue-700 rounded-full px-3 py-1">{(user?.name || '').split(' ').pop()?.toUpperCase()} {(user?.name || '').split(' ').slice(0, -1).join(' ')}</div>
        </div>
      </header>

      <div className="pt-[56px] flex min-h-screen">
        {/* ═══ LEFT: Activity Timeline ═══ */}
        <aside className="hidden lg:block w-72 border-r border-slate-100 bg-white p-4 overflow-y-auto" data-testid="activity-timeline">
          <button className="flex items-center gap-2 text-blue-600 hover:underline text-sm mb-6"
            onClick={() => navigate('/apprenant/sequences')} data-testid="quit-btn">
            <ArrowLeft className="h-4 w-4" />Quitter
          </button>

          <div className="space-y-0">
            {activities.map((act, i) => {
              const isCompleted = completedActs.includes(act.activity_id);
              const isCurrent = i === currentIdx;
              const isSubmission = act.type === 'realisation' || act.type === 'other';
              return (
                <div key={act.activity_id || i} className="flex items-start gap-3 cursor-pointer" onClick={() => goToActivity(i)} data-testid={`timeline-activity-${i}`}>
                  {/* Vertical line + icon */}
                  <div className="flex flex-col items-center">
                    <div className={cn(
                      "w-9 h-9 rounded-full flex items-center justify-center shrink-0 border-2 transition-colors",
                      isCompleted ? "bg-emerald-50 border-emerald-400" :
                      isCurrent ? "bg-blue-600 border-blue-600" :
                      isSubmission ? "bg-slate-50 border-slate-300" :
                      "bg-white border-slate-200"
                    )}>
                      {isCompleted ? (
                        <Check className="h-4 w-4 text-emerald-500" />
                      ) : isSubmission ? (
                        <Pencil className="h-4 w-4 text-slate-400" />
                      ) : isCurrent ? (
                        <Check className="h-4 w-4 text-white" />
                      ) : (
                        <span className="text-xs text-slate-400 font-semibold">{i + 1}</span>
                      )}
                    </div>
                    {i < activities.length - 1 && (
                      <div className={cn("w-0.5 h-8", isCompleted ? "bg-emerald-300" : "bg-slate-200")} />
                    )}
                  </div>
                  {/* Label */}
                  <div className={cn(
                    "rounded-lg px-3 py-2 mt-0.5 text-sm leading-snug transition-colors",
                    isCurrent ? "bg-indigo-100 text-indigo-700 font-semibold" :
                    isCompleted ? "text-slate-700" : "text-slate-500"
                  )}>
                    {i + 1}. {act.title}
                  </div>
                </div>
              );
            })}
          </div>
        </aside>

        {/* ═══ CENTER: Content ═══ */}
        <main className="flex-1 max-w-3xl mx-auto" data-testid="activity-content">
          {/* Banner */}
          <div className="h-56 bg-gradient-to-br from-indigo-300 to-indigo-400 flex items-center justify-center">
            <span className="text-7xl font-light text-white/50">{firstLetter}</span>
          </div>

          {/* Title + progress */}
          <div className="px-6 py-6 border-b border-slate-100">
            <h1 className="text-xl font-extrabold text-slate-900 text-center mb-3" data-testid="sequence-title">{sequence.title}</h1>
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-600 rounded-full" style={{ width: `${progress}%` }} />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Loader2 className="h-4 w-4 text-indigo-500" />
                </div>
                <span className="text-sm text-slate-500">Progression de la séquence</span>
                <span className="text-sm font-bold text-blue-600">{progress} %</span>
              </div>
              <Button variant="outline" size="sm" className="border-blue-600 text-blue-600 text-xs gap-1.5 rounded-lg" data-testid="agrandir-btn">
                <Maximize2 className="h-3.5 w-3.5" />Agrandir
              </Button>
            </div>
          </div>

          {/* Current activity content */}
          {currentActivity ? (
            <div className="px-6 py-8" data-testid="current-activity">
              <h2 className="text-2xl font-extrabold text-slate-900 mb-4">
                {currentActivity.title}
              </h2>

              {currentActivity.description && (
                <div className="prose prose-slate max-w-none text-slate-700 mb-6 whitespace-pre-wrap">
                  {currentActivity.description}
                </div>
              )}

              {currentActivity.content_url && (
                <div className="mb-6 p-4 bg-slate-50 rounded-xl">
                  <a href={currentActivity.content_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-2">
                    <Download className="h-4 w-4" />Ouvrir la ressource
                  </a>
                </div>
              )}

              {/* File upload for submission activities */}
              <div className="mt-6 space-y-3">
                <label className="cursor-pointer">
                  <input type="file" className="hidden" onChange={(e) => { if (e.target.files[0]) handleFileUpload(e.target.files[0]); e.target.value = ''; }} data-testid="activity-file-input" />
                  <span className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium transition-colors">
                    {uploadingFile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    Choisissez un autre fichier
                  </span>
                </label>
              </div>

              {/* Info about grading */}
              {(currentActivity.type === 'realisation' || currentActivity.type === 'other') && (
                <div className="mt-4 flex items-center gap-2 text-sm text-slate-400">
                  <Info className="h-4 w-4" />
                  <span>La réalisation n'a pas encore été notée par l'intervenant.</span>
                </div>
              )}
            </div>
          ) : (
            <div className="px-6 py-16 text-center">
              <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">{activities.length === 0 ? 'Aucune activité dans cette séquence' : 'Sélectionnez une activité'}</p>
            </div>
          )}

          {/* Bottom navigation */}
          {activities.length > 0 && (
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50" data-testid="activity-nav">
              <Button variant="outline" disabled={currentIdx === 0} className="gap-1.5 rounded-lg border-slate-300"
                onClick={() => goToActivity(Math.max(0, currentIdx - 1))} data-testid="prev-activity-btn">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <p className="text-sm text-blue-600 font-medium text-center flex-1 truncate px-4">
                {currentActivity ? `${currentIdx + 1}. ${currentActivity.title}` : ''}
              </p>
              <Button className="bg-white hover:bg-blue-700 hover:text-white border border-blue-600 text-blue-600 gap-1.5 rounded-lg"
                onClick={completeAndNext} data-testid="next-activity-btn">
                Continuer <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </main>

        {/* ═══ RIGHT: Discussions ═══ */}
        <aside className="hidden xl:block w-80 border-l border-slate-100 bg-white p-5 overflow-y-auto" data-testid="discussions-panel">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            <h3 className="font-bold text-slate-900">Discussions</h3>
          </div>

          <div className="bg-slate-50 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                <span className="text-rose-500 text-xs font-bold">F</span>
              </div>
              <p className="text-sm text-slate-600">Votre formateur est là pour répondre à vos questions, n'hésitez pas à le solliciter !</p>
            </div>
          </div>

          {/* Messages */}
          <div className="space-y-3 mb-4 max-h-80 overflow-y-auto">
            {discussions.map((msg, i) => (
              <div key={msg.msg_id || i} className={cn("p-3 rounded-xl text-sm", msg.user_id === user?.user_id ? "bg-blue-50 ml-4" : "bg-slate-50 mr-4")} data-testid={`discussion-msg-${i}`}>
                <p className="font-medium text-slate-700 text-xs mb-1">{msg.user_name}</p>
                <p className="text-slate-600">{msg.message}</p>
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="flex items-center gap-2">
            <Input placeholder="Nouvelle discussion" value={newMsg} onChange={(e) => setNewMsg(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()} className="h-10 text-sm" data-testid="discussion-input" />
            <Button size="icon" className="bg-blue-700 hover:bg-blue-800 h-10 w-10 shrink-0" onClick={sendMessage} disabled={sendingMsg} data-testid="send-msg-btn">
              <Send className="h-4 w-4 text-white" />
            </Button>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default LearnerSequencePlayer;
